import * as R from 'ramda'
import {
  AddAdmin,
  AddMembers,
  ChangeAdminPermissions,
  ChangeMemberPermissions,
  ChannelAdminChangeablePermissions,
  CheckHallSubscribers,
  GetBannedUsers,
  GetHallAdmins,
  GetHallExceptionMembers,
  GetHallSubscribers,
  GroupAdminChangeablePermissions,
  GroupMemberChangeablePermissions,
  HallAdminChangeablePermissions,
  HandleJoinRequests,
  PermitBannedUsers,
  RemoveAdmins,
  RemoveMembers,
  reqAddAdmin,
  reqAddMembers,
  reqChangeAdminPermissions,
  reqChangeMemberPermissions,
  reqCheckHallSubscribers,
  reqGetBannedUsers,
  reqGetHallAdmins,
  reqGetHallExceptionMembers,
  reqGetHallSubscribers,
  reqHandleJoinRequests,
  reqPermitBannedUsers,
  reqRemoveAdmins,
  reqRemoveMembers,
  reqResetMemberPermissions,
  ResetMemberPermissions,
  Subscriber,
  UserPermission,
} from '@weblite/ts-action/subscription'
import { decodeSubscriptionPermission, LIMITS } from '@weblite/permission'
import {
  GetJoinRequests,
  HallPermissions,
  reqGetJoinRequests,
  ResGetJoinRequestsPayload,
} from '@weblite/ts-action/hall'
import { request } from '$setup/ws'
import * as hallService from '../hall'
import { getHall, getOrCreateFirstAvailableInviteLink } from '../hall'
import {
  showHallAdminsLimitSnackbar,
  showNMemberRemovedFromHallSnackbar,
  showUserPromotedToAdminSnackbar,
} from '$comp/snackbars'
import { getSubscription } from '$logic/subscription/client.service'
import { showSendInviteLinkDialog } from '$comp/modals/send-invite-link'
import { updateHallAdminsCountAction } from '$common/hall-admin-action-item'
import { JoinRequestStatusFilter, joinRequestStatusFilterMap } from './types'
import { PackageLimitReachedType } from '$comp/modals/package-limit-reached'
import { showPackageLimitReachedDialog } from '$comp/modals/package-limit-reached'
import {
  ChannelManager,
  GetChannelManagers,
  reqGetChannelManagers,
  reqSetQuestionSupport,
  SetQuestionSupport,
} from './question-support.action'

export async function getHallSubscribers(
  roomId: string,
  pageToken?: string,
  query?: string,
): Promise<{ users: Subscriber[]; pageToken?: string }> {
  const res = await request<GetHallSubscribers>(
    reqGetHallSubscribers({ roomId, pageToken, query }),
  )
  if (res.error) return { users: [], pageToken: undefined }
  return res.payload
}

export async function getJoinHallRequests(
  roomId: string,
  pageToken?: string,
  query?: string,
  statusFilter = JoinRequestStatusFilter.PENDING,
): Promise<ResGetJoinRequestsPayload> {
  const res = await request<GetJoinRequests>(
    reqGetJoinRequests({
      roomId,
      pageToken,
      query,
      approved: joinRequestStatusFilterMap[statusFilter],
    }),
  )
  if (res.error) throw new Error(res.error.code)
  return res.payload
}

export async function handleJoinHallRequests(
  roomId: string,
  requests: {
    approved: boolean
    link: string
    userId: string
  }[],
  closeModal?: () => void,
) {
  const res = await request<HandleJoinRequests>(
    reqHandleJoinRequests({ roomId, requests }),
  )
  if (res.error) {
    if (res.error.code === 'USAGE_LIMIT_REACHED') {
      closeModal?.()
      showPackageLimitReachedDialog(PackageLimitReachedType.ADD_MEMBERS)
      return { success: false, showSnackbar: false }
    }
    return { success: false, showSnackbar: true }
  }

  return { success: true, showSnackbar: false }
}

export async function searchInSubscribers(roomId: string, query: string) {
  const res = await getHallSubscribers(roomId, undefined, query)
  return res.users
}

// TODO: Use #pagination
export async function getHallRemovedSubscribers(roomId: string) {
  const res = await request<GetBannedUsers>(reqGetBannedUsers({ roomId }))
  if (res.error) return null
  return res.payload?.users || []
}

export async function getHallAdmins(roomId: string) {
  const res = await request<GetHallAdmins>(reqGetHallAdmins({ roomId }))
  if (res.error) return []

  const hall = await getHall(roomId)

  return res.payload.admins.map(admin => ({
    ...admin,
    decodedPermissions: decodeSubscriptionPermission(
      hall.permissions,
      admin.permissions,
    ),
  }))
}

export async function getChannelManagers(
  roomId: string,
): Promise<ChannelManager[]> {
  const res = await request<GetChannelManagers>(
    reqGetChannelManagers({ roomId }),
  )
  if (res.error) return []

  const hall = await getHall(roomId)

  return res.payload.managers.map(manager => ({
    ...manager,
    decodedPermissions: {
      ...decodeSubscriptionPermission(hall.permissions, manager.permissions),
      questionSupport: manager.role === 'QUESTION_SUPPORT',
    },
  }))
}

export async function getChannelManager(roomId: string, userId: string) {
  const managers = await getChannelManagers(roomId)
  return managers.find(R.propEq('id', userId))
}

export async function setQuestionSupport(
  roomId: string,
  userId: string,
  enabled: boolean,
) {
  const res = await request<SetQuestionSupport>(
    reqSetQuestionSupport({ roomId, userId, enabled }),
  )

  return !res.error
}

// TODO: #optimize
export async function getAdminPermissions(hallId: string, userId: string) {
  const admins = await getHallAdmins(hallId)
  const admin = admins.find(R.propEq('id', userId))
  return admin?.permissions
}

export async function changeAdminPermissions(
  roomId: string,
  adminUserId: string,
  permissions: HallAdminChangeablePermissions,
): Promise<boolean> {
  const res = await request<ChangeAdminPermissions>(
    reqChangeAdminPermissions({ roomId, permissions, adminUserId }),
  )
  if (res.error) return false
  return true
}

export async function addAdmin(
  roomId: string,
  adminUserId: string,
  permissions: HallAdminChangeablePermissions,
): Promise<boolean> {
  const admins = await getHallAdmins(roomId)
  if (admins.length >= LIMITS.hallAdminsCount) {
    showHallAdminsLimitSnackbar()
    return false
  }
  const res = await request<AddAdmin>(
    reqAddAdmin({ roomId, adminUserId, permissions }),
  )
  if (res.error) return false
  showUserPromotedToAdminSnackbar(adminUserId)
  return true
}

export async function removeAdmins(
  roomId: string,
  adminUserIds: string[],
): Promise<boolean> {
  try {
    const res = await request<RemoveAdmins>(
      reqRemoveAdmins({ roomId, adminUserIds }),
    )
    updateHallAdminsCountAction.invoke()

    if (res.error) {
      if (res.error.code === 'USAGE_LIMIT_REACHED') {
        showPackageLimitReachedDialog(PackageLimitReachedType.REMOVE_ADMIN)
      }
      return false
    }

    return !res.error
  } catch (e) {
    return false
  }
}

export async function addMembers(
  roomId: string,
  userIds: string[],
  closeOpenedModal?: () => unknown,
): Promise<boolean> {
  const res = await request<AddMembers>(reqAddMembers({ roomId, userIds }))
  if (res.error) {
    if (res.error.code === 'USAGE_LIMIT_REACHED') {
      await closeOpenedModal?.()
      showPackageLimitReachedDialog(PackageLimitReachedType.ADD_MEMBERS)
    }
    return false
  }
  const { restrictedIds, hallBannedIds } = res.payload || {}
  if (restrictedIds?.length || hallBannedIds?.length) {
    const inviteLink = restrictedIds?.length
      ? await getOrCreateFirstAvailableInviteLink(roomId, restrictedIds.length)
      : null
    // if(!inviteLink) snackbar(t('serverError')) // TODO: fix me
    showSendInviteLinkDialog({
      ...res.payload,
      roomId,
      inviteLink: inviteLink?.link,
    })
  }

  closeOpenedModal?.()
  return true
}

export async function removeMembers(
  roomId: string,
  userIds: string[],
): Promise<boolean> {
  const res = await request<RemoveMembers>(
    reqRemoveMembers({ roomId, userIds }),
  )
  if (res.error) return false

  showNMemberRemovedFromHallSnackbar(userIds.length, roomId)
  return true
}

export async function changeMemberPermissions(
  roomId: string,
  userId: string,
  permissions: GroupMemberChangeablePermissions,
): Promise<boolean> {
  const res = await request<ChangeMemberPermissions>(
    reqChangeMemberPermissions({ roomId, userId, permissions }),
  )
  if (res.error) return false
  return true
}

export async function getHallExceptionMembers(
  roomId: string,
): Promise<UserPermission[]> {
  const res = await request<GetHallExceptionMembers>(
    reqGetHallExceptionMembers({ roomId }),
  )
  if (res.error) return []
  return res.payload.exceptions
}

// TODO: #optimize
export async function getExceptionMemberPermission(
  hallId: string,
  userId: string,
) {
  const members = await getHallExceptionMembers(hallId)
  const member = members.find(R.propEq('id', userId))
  return member?.permissions
}

export async function resetMemberPermissions(
  roomId: string,
  userId: string,
): Promise<boolean> {
  const res = await request<ResetMemberPermissions>(
    reqResetMemberPermissions({ roomId, userId }),
  )
  if (res.error) return false
  return true
}

export async function getBannedUsers(roomId: string) {
  const res = await request<GetBannedUsers>(reqGetBannedUsers({ roomId }))

  if (res.error) return []
  return res.payload.users
}

export async function permitBannedUsers(
  roomId: string,
  userIds: string[],
): Promise<boolean> {
  const res = await request<PermitBannedUsers>(
    reqPermitBannedUsers({ roomId, userIds }),
  )

  if (res.error) return false
  return true
}

function decodeOtherUserPermissions<
  T extends Partial<HallPermissions & { isAdmin: boolean }>,
>(pickedKeys: (keyof T)[]) {
  return async (hallId: string, permissions: number) => {
    const hall = await hallService.getHall(hallId)

    const { raw, ...decodedPermissions } = decodeSubscriptionPermission(
      hall?.permissions,
      permissions,
    )

    return R.pick(pickedKeys)(decodedPermissions) as unknown as T
  }
}

export const decodeGroupMemberPermissions =
  decodeOtherUserPermissions<GroupMemberChangeablePermissions>([
    'addMembers',
    'changeHallInfo',
    'sendMedia',
    'sendMessage',
    'sendWapp',
  ])

export const decodeGroupAdminPermissions =
  decodeOtherUserPermissions<GroupAdminChangeablePermissions>([
    'addNewAdmins',
    'banUsers',
    'changeHallInfo',
    'deleteMessages',
    'inviteViaLink',
    'pinMessage',
  ])

export const decodeChannelAdminPermissions =
  decodeOtherUserPermissions<ChannelAdminChangeablePermissions>([
    'addMembers',
    'addNewAdmins',
    'changeHallInfo',
    'deleteMessages',
    'editMessage',
    'sendMedia',
    'sendMessage',
    'sendWapp',
  ])

export async function checkHallSubscribersList(
  roomId: string,
  userIds: string[],
): Promise<string[]> {
  const res = await request<CheckHallSubscribers>(
    reqCheckHallSubscribers({
      roomId,
      userIds,
    }),
  )
  if ('error' in res) return []
  return res.payload.subscribers
}

export const getSubscriptionDate = async (roomId: string) => {
  const subscription = await getSubscription(roomId)
  return subscription?.createdAt
}
